import { useState } from "react";
import { MessageSquare, Mail, Share2, Copy, Check, X } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "../lib/supabase";
import { buildMessage, channelHrefs, jobLink } from "../lib/share";
import { useWallet } from "../hooks/useWallet";
import { markQueuedSent, removeOutbox } from "../lib/outbox";

const WhatsAppIcon = () => <span className="font-black text-sm">W</span>;

export const SendSheet = ({ dispatch, onStatus, onClose, onCancel }) => {
  const [busy, setBusy] = useState(false);
  const { refresh: refreshWallet } = useWallet() || {};
  const hrefs = channelHrefs(dispatch, dispatch.recipient_phone, dispatch.recipient_email);
  const sentLike = ["sent", "viewed", "completed", "queued_sent"].includes(dispatch.status);
  const link = jobLink(dispatch.job_token);
  const canCancel = dispatch.status === "draft" || dispatch.queued;

  const markSent = async () => {
    if (dispatch.queued) { if (!dispatch.outbox?.markSent) { await markQueuedSent(dispatch.outbox.idempotency_key); onStatus({ ...dispatch, status: "queued_sent", outbox: { ...dispatch.outbox, markSent: true } }); } return; }
    if (dispatch.status !== "draft") return;
    const { data, error } = await supabase.from("dispatches").update({ status: "sent" }).eq("id", dispatch.id).select().single();
    if (error) throw error;
    onStatus(data);
  };

  const go = async (href) => {
    try { await markSent(); window.location.href = href; } catch (e) { toast.error(e.message); }
  };

  const copyText = async (text) => {
    try { await navigator.clipboard.writeText(text); toast.success("Link copied"); }
    catch { window.prompt("Copy this link", text); }
  };

  const copy = async () => {
    try { await markSent(); await copyText(link); } catch (e) { toast.error(e.message); }
  };

  const share = async () => {
    try {
      await markSent();
      if (navigator.share) await navigator.share({ title: dispatch.title, text: buildMessage(dispatch), url: link });
      else await copyText(link);
    } catch (e) { if (e.name !== "AbortError") toast.error(e.message); }
  };

  const cancel = async () => {
    setBusy(true);
    if (dispatch.queued) { await removeOutbox(dispatch.outbox.idempotency_key); setBusy(false); toast.success("Queued job removed"); onCancel?.(dispatch); return; }
    const { data, error } = await supabase.from("dispatches").update({ status: "canceled" }).eq("id", dispatch.id).select().single();
    setBusy(false);
    if (error) return toast.error(error.message.includes("CANNOT_CANCEL_SENT") ? "Already sent — can't cancel." : error.message);
    toast.success("Credit returned"); onStatus(data); onCancel?.(data);
    refreshWallet?.(); setTimeout(() => refreshWallet?.(), 1500);
  };

  return (
    <div className="sheet-backdrop" onClick={onClose} data-testid="send-sheet-backdrop">
      <div className="sheet" onClick={(e) => e.stopPropagation()} data-testid="send-sheet">
        <div className="flex items-center justify-between">
          <p className="eyebrow">{sentLike ? "Sent ✓" : "Send job link"}</p>
          <button type="button" className="icon-btn" onClick={onClose} data-testid="send-sheet-close" aria-label="Close"><X size={16} /></button>
        </div>
        <h2 className="card-title">{dispatch.title}</h2>
        <p className="text-xs text-muted mt-1 break-all" data-testid="send-link">{link}</p>
        {dispatch.queued && <p className="text-xs text-[#F6C945] mt-2" data-testid="queued-notice">Link goes live once you're back online.</p>}
        <div className="grid grid-cols-2 gap-3 mt-4">
          {hrefs.sms && <a href={hrefs.sms} onClick={(e) => { e.preventDefault(); go(hrefs.sms); }} className="send-btn" data-testid="send-sms"><MessageSquare size={20} /> SMS</a>}
          {hrefs.whatsapp && <a href={hrefs.whatsapp} onClick={(e) => { e.preventDefault(); go(hrefs.whatsapp); }} className="send-btn send-wa" data-testid="send-whatsapp"><WhatsAppIcon /> <span className="notranslate" translate="no">WhatsApp</span></a>}
          {hrefs.email && <a href={hrefs.email} onClick={(e) => { e.preventDefault(); go(hrefs.email); }} className="send-btn" data-testid="send-email"><Mail size={20} /> Email</a>}
          <button type="button" className="send-btn" onClick={share} data-testid="send-share"><Share2 size={20} /> Share</button>
          <button type="button" className="send-btn" onClick={copy} data-testid="send-copy"><Copy size={20} /> Copy link</button>
        </div>
        <div className="flex gap-3 mt-5">
          {canCancel && (
            <button type="button" className="btn-secondary text-[#FF7A7A]" onClick={cancel} disabled={busy} data-testid="cancel-job-button">Cancel job</button>
          )}
          <button type="button" className="btn-primary flex items-center justify-center gap-2" onClick={onClose} data-testid="send-sheet-done"><Check size={16} /> Done</button>
        </div>
      </div>
    </div>
  );
};
